import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import values from 'lodash/values';
import get from 'lodash/get';
import classnames from 'classnames';
import { Button } from '@els/els-react--button';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import NumberFormatter from '@Utils/NumberFormatter';
import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import { v4 as uuid } from 'uuid';

import './SubstanceRightHandLinks.scss';

function checkLinkCounts(links) {
  return values(links)?.some((count) => count > 0);
}

function capitalize(string = '') {
  return string.length > 0 ? string[0].toUpperCase() + string.slice(1) : '';
}

function SubstanceRightHandLinks({ metadata = {}, substance, className, linkClassName }) {
  const links = get(substance, 'links', {});

  const openElementLink = useCallback(
    (type) => {
      const requestParams = {
        hitSetKey: metadata.hitSetKey,
        resultName: metadata.resultName,
        searchId: metadata.searchId || uuid(),
      };
      if (metadata.order) {
        requestParams.order = metadata.order;
      }
      SubstanceHelper.openElementLink(type, substance, requestParams);
    },
    [metadata, substance]
  );

  const renderLink = (count, type) =>
    count > 0 && (
      <Button
        data-e2e="substance-right-hand-link"
        type="link"
        linkOptions={['inline', 'navigation']}
        key={type}
        className={classnames('substance-right-hand-link', `e2e-righthand-${type}`, linkClassName)}
        onClick={() => openElementLink(type)}>
        {capitalize(type)} - {NumberFormatter(count)}
      </Button>
    );

  return checkLinkCounts(links) ? (
    // RX-23573: not a `nav` landmark. Rendered once per result row, so a landmark here multiplied
    // into dozens of identical "Related substance results" navigation landmarks — landmark
    // overuse (ARIA APG). The links remain reachable in normal reading order.
    <div className={classnames('substance-related-links', className)}>
      {renderLink(links.preparationsCount, 'preparations')}
      {renderLink(links.reactionsCount, 'reactions')}
      {renderLink(links.targetsCount, 'targets')}
      {renderLink(links.documentCount, 'documents')}
    </div>
  ) : null;
}

SubstanceRightHandLinks.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.shape({
    links: PropTypes.shape({
      preparationsCount: PropTypes.number,
      reactionsCount: PropTypes.number,
      targetsCount: PropTypes.number,
      documentCount: PropTypes.number,
    }),
  }).isRequired,
  className: PropTypes.string,
  linkClassName: PropTypes.string,
};

export default SubstanceRightHandLinks;
